import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, Play, Pause, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEDULE_LABELS = {
    daily: "Daily",
    weekdays: "Weekdays",
    weekly: "Weekly (Mondays)",
    hourly: "Hourly (test)",
};

function fmtNext(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
}

export default function SavedSearchesPanel({ onRunStarted }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        category: "Plumbers",
        location: "Auckland",
        schedule: "daily",
        run_at_local: "02:00",
        source: "all",
        max_results: 500,
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/saved-searches`);
            setItems(data || []);
        } catch (e) {
            console.error("saved-searches list fail", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.category.trim() || !form.location.trim()) return;
        try {
            await axios.post(`${API}/saved-searches`, form);
            toast.success("Saved search created");
            setForm({ ...form, category: "", location: "" });
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed to create";
            toast.error(String(msg));
        }
    };

    const toggleActive = async (s) => {
        try {
            await axios.patch(`${API}/saved-searches/${s.id}`, { active: !s.active });
            load();
        } catch {
            toast.error("Failed to toggle");
        }
    };

    const del = async (id) => {
        try {
            await axios.delete(`${API}/saved-searches/${id}`);
            load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const runNow = async (id) => {
        try {
            const { data } = await axios.post(`${API}/saved-searches/${id}/run`);
            toast.success(`Run started (${data.run_id.slice(0, 8)}…)`);
            onRunStarted?.(data.run_id);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed to run";
            toast.error(String(msg));
        }
    };

    return (
        <div className="panel" data-testid="saved-searches-panel">
            <div className="panel-header">
                <div className="flex items-center gap-2">
                    <Clock size={14} weight="bold" />
                    <span className="label-kicker">Scheduled Saved Searches</span>
                    <span className="mono text-[11px] text-[#52525b]">
                        {items.length} {items.length === 1 ? "search" : "searches"}
                    </span>
                </div>
            </div>

            <form
                onSubmit={submit}
                className="p-4 grid grid-cols-1 md:grid-cols-6 gap-2 items-end border-b border-[#e4e4e7]"
                data-testid="saved-search-form"
            >
                <input
                    type="text"
                    placeholder="Category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="brutal-input px-2 py-1.5 text-sm"
                    data-testid="ss-category"
                    required
                />
                <input
                    type="text"
                    placeholder="Location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="brutal-input px-2 py-1.5 text-sm"
                    data-testid="ss-location"
                    required
                />
                <select
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    className="brutal-input px-2 py-1.5 text-sm"
                    data-testid="ss-source"
                >
                    <option value="all">All sources</option>
                    <option value="directories">Directories</option>
                    <option value="yellow">yellow</option>
                    <option value="finda">finda</option>
                    <option value="google">web</option>
                </select>
                <select
                    value={form.schedule}
                    onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                    className="brutal-input px-2 py-1.5 text-sm"
                    data-testid="ss-schedule"
                >
                    <option value="daily">Daily</option>
                    <option value="weekdays">Weekdays</option>
                    <option value="weekly">Weekly</option>
                    <option value="hourly">Hourly (test)</option>
                </select>
                <input
                    type="text"
                    placeholder="02:00"
                    pattern="^\d{2}:\d{2}$"
                    value={form.run_at_local}
                    onChange={(e) =>
                        setForm({ ...form, run_at_local: e.target.value })
                    }
                    className="brutal-input px-2 py-1.5 text-sm mono"
                    data-testid="ss-time"
                />
                <button
                    type="submit"
                    className="btn-primary"
                    style={{ padding: "7px 10px", fontSize: 12 }}
                    data-testid="ss-add"
                >
                    <Plus size={12} weight="bold" />
                    Add
                </button>
            </form>

            <div className="divide-y divide-[#e4e4e7]">
                {loading && (
                    <div className="p-5 text-[11px] text-[#52525b]">Loading…</div>
                )}
                {!loading && items.length === 0 && (
                    <div className="p-5 text-[11px] text-[#52525b]">
                        No saved searches yet. Add one above to schedule nightly
                        pre-crawls.
                    </div>
                )}
                {items.map((s) => (
                    <div
                        key={s.id}
                        className="p-3 flex items-center justify-between gap-3 flex-wrap"
                        data-testid={`saved-search-${s.id}`}
                    >
                        <div className="flex flex-col min-w-0">
                            <span className="font-medium text-sm">
                                {s.category} · {s.location}
                            </span>
                            <span className="mono text-[10px] text-[#52525b]">
                                {s.source} · {SCHEDULE_LABELS[s.schedule] || s.schedule}{" "}
                                @ {s.run_at_local} · next {fmtNext(s.next_run_at)}
                                {s.last_run_at
                                    ? ` · last ${fmtNext(s.last_run_at)}`
                                    : ""}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => runNow(s.id)}
                                className="btn-secondary"
                                style={{ padding: "5px 8px", fontSize: 11 }}
                                data-testid={`ss-run-${s.id}`}
                                disabled={!s.active}
                                title="Run now"
                            >
                                <Play size={11} weight="bold" />
                                Run now
                            </button>
                            <button
                                onClick={() => toggleActive(s)}
                                className="btn-secondary"
                                style={{ padding: "5px 8px", fontSize: 11 }}
                                data-testid={`ss-toggle-${s.id}`}
                                title={s.active ? "Pause" : "Activate"}
                            >
                                {s.active ? (
                                    <>
                                        <Pause size={11} weight="bold" />
                                        Active
                                    </>
                                ) : (
                                    <>
                                        <Play size={11} weight="bold" />
                                        Paused
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => del(s.id)}
                                className="btn-ghost"
                                data-testid={`ss-delete-${s.id}`}
                                aria-label="Delete saved search"
                            >
                                <Trash size={13} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
